import { useEffect } from 'react';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';
// components
import { TAgent } from '@newo/api/gateway';
import Header from '@/modules/Sandbox/components/Header';
import Footer from '@/modules/Chat/components/Footer';
import Content from '@/modules/Sandbox/components/Content';
// utils
import { IChatMessage, IParticipants } from '@/modules/Chat/Chat.types.ts';
import useSandboxStore from '@/modules/Sandbox/Sandbox.store.ts';
import { useChatHistory, useMessages } from '@/modules/Chat/Chat.hooks.ts';
import { usePersonaQuery } from '@/services/bff/bff.queries.ts';
// assets
import styles from '@/modules/Sandbox/Sandbox.module.css';

interface ChatProps {
    agent?: TAgent;
    className?: string;
    onSend?: () => void;
    onChange?: () => void;
}

const Sandbox = ({ agent, className, onSend, onChange }: ChatProps) => {
    const participants: IParticipants = useSandboxStore(
        useShallow((state) => ({
            agentPersonaId: state.agentPersona?.id,
            agentActorId: state.agentActor?.id,
            userPersonaId: state.userPersona?.id,
            userActorId: state.userActor?.id
        }))
    );

    const { data: agentPersona, isFetching: isPersonaFetching } =
        usePersonaQuery(agent?.persona_id);

    const { acts, refetch, isFetching } = useChatHistory(participants);
    const handleRefetch = () => {
        refetch();
    };

    const setAgentPersona = useSandboxStore((state) => state.setAgentPersona);
    const { messages, setMessages } = useMessages({
        participants,
        onComplete: handleRefetch
    });

    useEffect(() => {
        setMessages([]);
    }, [acts, setMessages]);

    useEffect(() => {
        setAgentPersona(agentPersona);
    }, [agentPersona, setAgentPersona]);

    const handleSend = (payload: IChatMessage) => {
        setMessages([...messages, payload]);
        onSend?.();
    };

    return (
        <div className={clsx(styles.chat, className)}>
            <Header agentId={agent?.id} isFetching={isPersonaFetching} />
            <Content acts={acts} messages={messages} />
            <Footer
                isDisabled={!participants.userActorId}
                isFetching={isFetching}
                refetch={handleRefetch}
                userActorId={participants.userActorId}
                onChange={onChange}
                onSend={handleSend}
            />
        </div>
    );
};

export default Sandbox;
